
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tooltip, styled } from '@mui/material';

import {
    IPatient,
    IPatientSummaryActionProps,
    useSnackbar
} from '@ngt/opms';
import { FC, useCallback, useState } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { PatientStateType } from '@/api/dtos';
import { JsonServiceClient } from '@servicestack/client';


const StyledTooltip = styled(Tooltip)(() => ({
    display: 'flex'
}))

const StyledTooltipWrapper = styled(Box)(() => ({
    display: 'flex'
}))

const StyledEventButton = styled(Button)(({ theme }) => ({
    minWidth: 140,
    borderRadius: 0,
    whiteSpace: 'nowrap',
    '&:first-of-type': {
        marginLeft: theme.spacing(0)
    }
}))



export enum PatientSummaryDialogState {
    None = 0,
    Create = 1
}

const usePatientSummaryCreateActions = (
    updateTo: PatientStateType,
    hideButton: (patient: IPatient | null | undefined) => boolean,
    eventTitle: string,
    eventName: string,
    saving: boolean,
    updatePatientState: (state: PatientStateType, skipPatientEmail: boolean, patientStatusReason?: string) => void,
    enqueueSnackbar: ReturnType<typeof useSnackbar>['enqueueSnackbar'],
    _client: JsonServiceClient,
    patient: IPatient | null | undefined,
    patientValid: boolean
) => {
    const [dialogState, setDialogState] = useState(PatientSummaryDialogState.None);

    const openCreateDialog = useCallback(() => {
        setDialogState(PatientSummaryDialogState.Create);
    }, [setDialogState]);

    const closeDialog = useCallback(() => {
        setDialogState(PatientSummaryDialogState.None);
    }, [setDialogState]);

    const onCreateClick = useCallback(async () => {
        try {
            await updatePatientState(updateTo, true);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        {eventTitle} Event Created
                    </AlertTitle>
                    The {eventName} event was successfully created.
                </>,
                { variant: 'success' }
            );

            closeDialog();
        }
        catch {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        {eventTitle} Event Not Created
                    </AlertTitle>
                    An error occurred while attempting to create the {eventName} event.
                </>,
                { variant: 'critical' }
            );

            closeDialog();
        }
    }, [updatePatientState, enqueueSnackbar, updateTo, eventTitle, eventName, closeDialog, patient]);

    const action: FC<IPatientSummaryActionProps> = useCallback(({
        patient: actionPatient
    }) => {
        if (hideButton(actionPatient)) {
            return null;
        }

        const createButton = (
            <StyledEventButton
                loading={saving}
                variant="contained"
                color="primary"
                onClick={openCreateDialog}
                disabled={updateTo !== PatientStateType.EndOfTreatment && (!patientValid || saving)}
            >
                Create
            </StyledEventButton>
        );

        if (patientValid || updateTo === PatientStateType.EndOfTreatment) {
            return <>
                {createButton}
                <Dialog
                    open={dialogState === PatientSummaryDialogState.Create}
                    onClose={closeDialog}
                    aria-labelledby="create-dialog-title"
                    aria-describedby="create-dialog-description"
                >
                    <DialogTitle id="create-dialog-title">Create {eventTitle} Event?</DialogTitle>
                    <DialogContent>
                        {
                            <DialogContentText id="create-dialog-description">
                                Are you sure you wish to create the {eventName} event?
                            </DialogContentText>
                        }
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDialog} color="secondary">
                            No
                        </Button>
                        <Button onClick={onCreateClick} color="primary" variant="contained" autoFocus>
                            Yes
                        </Button>
                    </DialogActions>
                </Dialog>
            </>;
        }

        return (

            <StyledTooltip
                title="Complete the previous event to continue"
            >
                <StyledTooltipWrapper>
                    {createButton}
                </StyledTooltipWrapper>
            </StyledTooltip>
        );
    }, [onCreateClick, saving, patientValid, updateTo, hideButton, closeDialog, dialogState, eventName, eventTitle, openCreateDialog]);

    return action;
};

export default usePatientSummaryCreateActions;